import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle } from '@untitled-ui/icons-react'
import { BillingService } from '../billing.service'
import { WalletService, PricingService } from '../wallet.service'
import { MyHorizontalTabV2 } from '@interstellar-component'

import GolonganBadge from '../components/CompanyInformation/GolonganBadge'
import ConfirmPaymentModal from './ConfirmPaymentModal'
import ConfirmChangeModal from './ConfirmChangeModal'
import PlanCalculator from './PlanCalculator'
import { formatIDR } from '../billing.format'
import { golonganLabel } from '../wallet.format'

// Full-screen "Subscribe to a plan" page (replaces the slide-in SubscribePanel
// for NEW subscriptions). Lists packages from the BRD catalog (incl. Pay-as-you-go, a
// plan_type='subscription' plan with Rp0 platform fee), lets the user buy an
// initial quota (min 1) in the SAME payment, and POSTs /subscribe with
// enquiry_count. The combined first invoice = platform fee + quota; on success
// we redirect to the Xendit checkout returned by the API.

/**
 * Adapt `/pricing/resolve` into what GolonganBadge reads.
 *
 * Straight passthrough — resolve already returns these under the same names.
 * It does NOT carry `can_cancel`, so the withdraw affordance stays hidden here;
 * a purchase screen is where you see which price table applies to you, not
 * where you manage an application.
 */
const golonganFromPricing = (pricing) =>
  pricing && pricing.golongan_usaha
    ? {
      company_id: pricing.company_id,
      golongan_usaha: pricing.golongan_usaha,
      golongan_review_status: pricing.golongan_review_status,
    }
    : null

// ── Katalog: pricing_packages (BRD) ─────────────────────────────────────────
// Periode adalah PILIHAN di sini — tiap tier punya baris 30 hari dan 365 hari.
// Tabel lama mengunci periode di dalam tier (Starter bulanan, Growth 6 bulan,
// Business tahunan), yang membuat toggle Monthly/Annual mustahil.
const PERIODS = [
  { key: 30, label: 'Monthly billing' },
  { key: 365, label: 'Annual billing' },
]

/**
 * Headline = harga_periode_idr, yang harus PERSIS sama dengan yang ditagih.
 *
 * harga_periode_idr sudah memasukkan diskon tahunan. Bonus kuota GRATIS, BUKAN
 * priced-in (keputusan Dhimas 2026-09-08, core#64) — jadi harga_periode == base.
 * Headline = harga_periode = harga yang PERSIS ditagih; bonus tampil murni
 * sebagai perk gratis, bukan komponen harga.
 *
 *   30 hari : headline = harga_periode (per bulan == total periode)
 *   1 tahun : headline = harga_periode / 12 (biaya per bulan efektif); baris
 *             "Total saat bayar" menyebut total tahunannya, dan pembandingnya
 *             harga_periode baris 30 HARI tier yang sama (apple-to-apple, sama-
 *             sama sudah termasuk bonus) untuk badge "Hemat X%".
 */
function packagePrice(pkg, catalog) {
  if (pkg.is_payg) {
    return { amount: formatIDR(0), suffix: '/ month', periodTotal: null, periodLabel: null }
  }

  const total = Number(pkg.harga_periode_idr)

  if (Number(pkg.period_days) !== 365) {
    return {
      // Priced-in total, NOT base — so headline == what the customer pays and
      // there is no base-vs-total discrepancy to give the bonus away.
      amount: formatIDR(total),
      suffix: '/ month',
      // For a 30-day package the monthly headline IS the period total, so the
      // separate "Total saat bayar" line would just repeat it.
      periodTotal: null,
      periodLabel: pkg.period_label,
      monthlyCompare: null,
      savingPct: null,
    }
  }

  // Katalog selalu memuat kedua periode sekaligus (lihat listPackages di bawah),
  // jadi baris bulanannya ada tanpa fetch tambahan. Kalau toh hilang, kartu
  // turun ke bentuk lama — tanpa badge, bukan tanpa harga.
  const monthly = (catalog || []).find(
    (p) => !p.is_payg && p.tier === pkg.tier && Number(p.period_days) === 30,
  )
  const monthlyTotal = monthly ? Number(monthly.harga_periode_idr) : 0
  // Diskon tahunan = 40% TETAP (PRICING_ANNUAL_DISCOUNT), ditampilkan sebagai
  // label — BUKAN dihitung dari total/(bulanan×12). Perhitungan itu meleset ke
  // 41% ketika base tahunan sedikit di bawah base bulanan (paritas dengan compro
  // everest.co.id, yang badge "Hemat 40%"-nya juga label tetap). Kalau
  // PRICING_ANNUAL_DISCOUNT diubah, sesuaikan angka ini.
  const pct = monthlyTotal > 0 && total < monthlyTotal * 12 ? 40 : 0

  return {
    amount: formatIDR(Math.round(total / 12)),
    suffix: '/ month',
    periodTotal: formatIDR(total),
    periodLabel: pkg.period_label,
    monthlyCompare: monthlyTotal > 0 ? formatIDR(monthlyTotal) : null,
    savingPct: pct > 0 ? pct : null,
  }
}

// Tidak ada kolom is_recommended di pricing_packages. Desain menandai Business,
// jadi itu yang dipakai sampai kolomnya ada — begitu ada, ganti satu baris ini.
const isRecommended = (pkg) => !!pkg.is_recommended || pkg.tier === 'business'

/**
 * Label aksi per kartu (Figma: "Switch to this plan" / "Your current plan" /
 * "Upgrade to <tier>"). Arah ditentukan urutan tier, bukan harga, karena harga
 * tahunan dan bulanan tidak sebanding langsung.
 */
const TIER_ORDER = { payg: 0, starter: 1, growth: 2, business: 3 }

/**
 * Direction must match the server's `directionOf` exactly — tier first, then
 * the period axis as the tie-break within a tier. Getting it wrong here does
 * not change what is charged (the server re-derives everything), but it would
 * label a card "Upgrade" and then open a dialog saying "berlaku 11 September",
 * which is worse than no label.
 *
 * Deliberately NOT decided on price: tier rank and money rank disagree in both
 * directions. Business 30-hari -> Starter 1-tahun is a downgrade by tier while
 * costing 3,8x more, and the dialog says so out loud rather than hiding it.
 */
function directionFor(pkg, currentPackage) {
  if (!currentPackage) return null
  if (currentPackage.id === pkg.id) return 'same'
  const here = TIER_ORDER[pkg.tier] ?? 0
  const now = TIER_ORDER[currentPackage.tier] ?? 0
  if (here > now) return 'upgrade'
  if (here < now) return 'downgrade'
  const pa = Number(currentPackage.period_days) || 30
  const pb = Number(pkg.period_days) || 30
  if (pb > pa) return 'upgrade'
  if (pb < pa) return 'downgrade'
  return 'same'
}

function ctaFor(pkg, currentPackage, pendingPackageId, golonganLocked) {
  if (!currentPackage) return { label: 'Choose this plan', disabled: false }
  if (currentPackage.id === pkg.id) return { label: 'Your current plan', disabled: true }
  if (pendingPackageId && pendingPackageId === pkg.id) {
    return { label: 'Scheduled', disabled: true }
  }
  const dir = directionFor(pkg, currentPackage)
  const verb = dir === 'upgrade' ? 'Upgrade to' : 'Switch to'
  // Golongan changed while a paid sub is still on the old golongan's package:
  // the switch is locked until the boundary remap (or a cancel). Keep the label
  // so the grid still reads normally, but disable it — the banner says why.
  if (golonganLocked) return { label: `${verb} ${pkg.name.toLowerCase()}`, disabled: true, locked: true }
  return { label: `${verb} ${pkg.name.toLowerCase()}`, disabled: false }
}

export default function SubscribePage() {
  const { companyId } = useParams()
  const navigate = useNavigate()

  const [packages, setPackages] = useState([])
  const [selectedPlanId, setSelectedPlanId] = useState(null)
  // 30 | 365 — the Monthly/Annual axis the old table could not express.
  const [period, setPeriod] = useState(30)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Pressing a card's button opens the confirm dialog; quota, payment method,
  // saldo and every figure now live in there (Figma 15195:146799).
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Same press, different dialog: a company with no subscription is BUYING,
  // one that already has a package is MOVING, and the two price differently.
  const [changeOpen, setChangeOpen] = useState(false)

  // Rupiah wallet (BR-SALDO). Read once — the balance can only change from
  // somewhere else, and the server re-checks it under a lock when we submit.
  const [wallet, setWallet] = useState(null)

  // Which price table applies to this company. Read from /pricing/resolve
  // rather than the golongan endpoint on purpose: that one is gated
  // `company:view` while every other read on this page is `billing:view`, so a
  // finance-only user would 403 and the badge would vanish without a sound —
  // on the very screen where knowing your golongan decides what you pay.
  const [pricing, setPricing] = useState(null)

  // Drives the per-card action label. Same `billing:view` gate as everything
  // else on this page, so it cannot 403 for someone who can already see it.
  const [subscription, setSubscription] = useState(null)

  // Set when a subscribe was paid entirely from saldo — no gateway, no VA, so
  // there is nowhere to redirect and the page has to say so itself.
  const [settled, setSettled] = useState(null)

  useEffect(() => {
    if (!companyId) return
    PricingService.resolve(companyId)
      .then((res) => setPricing(res?.data ?? res))
      // A failed resolve only costs the badge; it must not block buying.
      .catch(() => setPricing(null))
    BillingService.getSubscription(companyId)
      .then((res) => setSubscription(res?.data ?? res))
      .catch(() => setSubscription(null))
  }, [companyId])

  useEffect(() => {
    if (!companyId) return
    WalletService.getWallet(companyId)
      .then((res) => setWallet(res?.data ?? res))
      // A wallet read failing must not block subscribing by card — it only
      // means the saldo option can't be offered.
      .catch(() => setWallet(null))
  }, [companyId])

  // Katalog datang lengkap untuk kedua periode sekaligus (6 baris korporat /
  // 7 baris UMKM), sudah di-scope golongan oleh server. Toggle periode karena
  // itu murni penyaring di klien — tidak perlu fetch ulang.
  useEffect(() => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    PricingService.listPackages(companyId, { limit: 50 })
      .then((res) => {
        const body = res?.data ?? res
        const list = (body?.data ?? body ?? []).slice()
        setPackages(list)
      })
      .catch((err) =>
        setError(err?.response?.data?.message || err.message || 'Failed to load plans'),
      )
      .finally(() => setLoading(false))
  }, [companyId])

  // PAYG has no period of its own (period_days NULL) — BR-SUB-11 — so it belongs
  // in both tabs rather than disappearing when Annual is selected.
  const visiblePackages = useMemo(
    () =>
      packages
        .filter((p) => p.is_payg || Number(p.period_days) === period)
        .sort((a, b) => (a.is_payg ? 0 : 1) - (b.is_payg ? 0 : 1) || 0),
    [packages, period],
  )

  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === selectedPlanId) || null,
    [packages, selectedPlanId],
  )

  // Which package the company is on right now — resolve() returns it directly,
  // so "Your current plan" needs no separate subscription read.
  const currentPackage = pricing?.package || null

  // BR-SUB-07 poin 3. Dibaca dari flag turunan server, BUKAN dari
  // `golongan_review_status` mentah: server yang memutuskan status review mana
  // yang berarti "sedang diproses" (pending_review DAN in_review), dan flag itu
  // sudah ikut memastikan perusahaannya memang masih korporat — lihat
  // core/service/pricing/local-pricing.js. Menghitung ulang aturannya di sini
  // berarti dua tempat yang bisa berbeda pendapat soal kapan tarif Korporat
  // masih berlaku.
  const umkmInFlight = !!pricing?.umkm_application_in_progress

  // Golongan changed while a paid subscription is still on the OLD golongan's
  // package (subscription.pricing_package carries the ACTUAL package; pricing
  // resolves the CURRENT golongan). Package-change is locked until the renewal
  // boundary remaps the sub to the same tier in the new golongan (or the sub is
  // cancelled) — the server enforces this too (change.js 409).
  const golonganLocked =
    !!subscription?.pricing_package &&
    !!pricing?.golongan_usaha &&
    subscription.pricing_package.golongan_usaha !== pricing.golongan_usaha

  const reloadSubscription = useCallback(() => {
    if (!companyId) return
    BillingService.getSubscription(companyId)
      .then((res) => setSubscription(res?.data ?? res))
      .catch(() => {})
    WalletService.getWallet(companyId)
      .then((res) => setWallet(res?.data ?? res))
      .catch(() => {})
  }, [companyId])

  // A change the customer already scheduled, resolved against the catalog this
  // page already loaded rather than fetched again.
  const scheduledPackage = useMemo(() => {
    const id = subscription?.pending_package_id
    if (!id) return null
    return packages.find((p) => p.id === id) || null
  }, [packages, subscription])

  // The boundary the scheduled change lands on. `current_period_start` is the
  // billing POINTER — the instant the next period begins — and NOT
  // `current_period_end`, which is the end of the period that will be billed
  // next and reads a full cycle too late.
  const scheduledOn = useMemo(() => {
    const at = subscription?.current_period_start
    if (!at) return null
    const d = new Date(at)
    return Number.isNaN(d.getTime())
      ? null
      : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  }, [subscription])

  const dropScheduledChange = useCallback(async () => {
    if (!companyId) return
    try {
      await BillingService.cancelScheduledChange(companyId)
      reloadSubscription()
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to cancel the scheduled change.')
    }
  }, [companyId, reloadSubscription])

  // Pressing a card's action selects it AND opens the confirm dialog — the
  // design has no separate "continue" step at page level.
  const onSelectPlan = (pkg) => {
    setSelectedPlanId(pkg.id)
    // A PAYG company has no real subscription row — `resolve` returns the
    // `umkm-payg` pseudo-package (is_payg), not null, so the naive
    // "has a current package" test sent PAYG -> paid through the change-plan
    // flow, which 404s ("No live subscription to change") and surfaces as
    // "Gagal menghitung perubahan paket". PAYG is the pre-subscription floor:
    // moving off it is BUYING the first subscription, not MOVING between paid
    // tiers. Route it to the subscribe modal.
    const hasPaidSubscription =
      currentPackage && !currentPackage.is_payg && currentPackage.id !== pkg.id
    if (hasPaidSubscription) {
      setChangeOpen(true)
      return
    }
    setConfirmOpen(true)
  }

  const goBackToCompany = useCallback(() => navigate('/company'), [navigate])

  // ── Paid entirely from saldo ────────────────────────────────────────────────
  // Deliberately a terminal state rather than an instant redirect: the money
  // moved and the plan is live, and that deserves to be read before the page
  // changes underneath the user.
  if (settled) {
    const inv = settled.invoice || {}
    const planName = settled.plan?.name || selectedPackage?.name || 'Plan'
    return (
      <main className="h-full w-full overflow-y-auto bg-gray-light/50">
        <div className="mx-auto flex max-w-[560px] flex-col items-center px-6 py-16 text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-success/100 text-success/600">
            <CheckCircle size={28} stroke="currentColor" />
          </span>
          <h1 className="mt-5 text-xl font-semibold text-gray-light/900">
            Your {planName} subscription is active
          </h1>
          <p className="mt-1.5 text-sm text-gray-light/600">
            Paid in full from saldo — there is no invoice left to pay.
          </p>

          <div className="mt-7 w-full rounded-2xl border border-gray-light/200 bg-white p-6 text-left shadow-xs">
            <div className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-gray-light/600">Paid from saldo</span>
              <span className="font-semibold text-gray-light/900">
                {formatIDR(inv.saldo_applied_idr)}
              </span>
            </div>
            <div className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-gray-light/600">Enquiry quota added</span>
              <span className="font-semibold text-gray-light/900">{inv.enquiry_count ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between border-t border-gray-light/200 pt-3 text-sm">
              <span className="text-gray-light/600">Invoice no.</span>
              <span className="font-mono text-xs font-semibold text-gray-light/900">
                {inv.invoice_number || '—'}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={goBackToCompany}
            className="mt-7 w-full rounded-xl bg-brand/600 px-4 py-3 text-sm font-semibold text-white shadow-xs hover:bg-brand/700"
          >
            Back to Company information
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className="h-full w-full overflow-y-auto bg-gray-light/50">
      <div className="mx-auto flex max-w-[1180px] flex-col gap-6 px-6 py-8">
        {/* ── Header (Figma 15186:191066) ─────────────────────────────────
            Back sebagai link tanpa border, judul 48px di tengah, dan badge
            golongan di atasnya. TIDAK ada divider di sini — satu-satunya garis
            di frame itu memisahkan grid kartu dari seksi Calculator. */}
        <div className="relative flex flex-col items-center gap-5 pt-1">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="absolute left-0 top-0 flex items-center gap-1.5 text-sm font-semibold text-gray-light/600 transition-colors hover:text-gray-light/900"
          >
            <ArrowLeft size={20} stroke="currentColor" />
            Back
          </button>

          {/* Golongan menentukan seluruh tabel harga (BR-SUB-08/09), jadi ia
              milik header halaman ini, bukan sekadar hiasan profil.
              Sumbernya /pricing/resolve — BUKAN endpoint golongan, yang
              digerbangi `company:view` sementara halaman ini `billing:view`.
              User berperan finansial-saja akan kena 403 di sana dan badge-nya
              hilang diam-diam justru saat ia sedang membeli. */}
          {pricing ? (
            <GolonganBadge status={golonganFromPricing(pricing)} onChanged={null} />
          ) : null}

          <h1 className="text-center text-4xl font-semibold tracking-tight text-gray-light/900 sm:text-5xl">
            Select your plan
          </h1>

          {/* Monthly / Annual — the period axis of the BRD catalog. Purely a
              client-side filter: the catalog already carries both periods.
              Uses the design-system tab so it matches every other segmented
              control in the app rather than being a one-off. */}
          {/* `MyHorizontalTabV2` matches on data-value as a STRING, so the
              period travels as one and converts back on change. */}
          <MyHorizontalTabV2
            value={String(period)}
            onChange={(v) => setPeriod(Number(v))}
            tabs={PERIODS.map((p) => ({ value: String(p.key), label: p.label }))}
            fitContent
          />
        </div>

        {error ? (
          <div className="rounded-md border border-error/200 bg-error/50 p-3 text-sm text-error/700">
            {error}
          </div>
        ) : null}

        {/* BR-SUB-07 poin 3 — dan peringatan ini harus terbaca SEBELUM orang
            menekan "Choose this plan", bukan sesudah membayar. Kalimat yang
            sama sudah ada di halaman pengajuan UMKM, tapi orang yang sampai ke
            layar ini datang untuk membeli: selisih tarif Korporat vs UMKM
            berlipat (Starter 1 tahun: Rp 11.880.000 vs Rp 774.000), dan yang
            sudah terbayar tidak dikembalikan saat pengajuannya disetujui.

            Sebelumnya satu-satunya tempat halaman ini menyebut aturan tersebut
            adalah kalimat "simulasi" di dalam kalkulator, yang baru muncul
            setelah orang menekan tab Segment — kontrol yang kebanyakan pembeli
            tidak pernah sentuh. Peringatan yang butuh interaksi untuk muncul
            sama saja dengan tidak ada.

            Angkanya sengaja tidak ditulis di sini: harga hidup di katalog
            (pricing_packages) dan kartu di bawah sudah menampilkannya. */}
        {umkmInFlight ? (
          <div className="flex gap-2.5 rounded-lg border border-warning/300 bg-warning/50 p-3.5">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning/700" />
            <p className="text-sm text-warning/700">
              Your UMKM application is being verified by the Everest admin, usually within
              1–3 business days. While you wait, the company stays Corporate and{' '}
              <span className="font-semibold">
                all prices on this page are Corporate rates
              </span>
              . Rates you have already paid{' '}
              <span className="font-semibold">are not adjusted</span> if the application is
              approved later — the difference is large, so consider waiting for the review
              result before buying.{' '}
              <button
                type="button"
                onClick={() => navigate(`/company/${companyId}/umkm`)}
                className="font-semibold underline"
              >
                View application status
              </button>
            </p>
          </div>
        ) : null}

        {/* What the company is on now, and anything already scheduled. The
            previous copy here told users to cancel their subscription from the
            Company information page in order to switch — wrong twice over: that
            page has no cancel action at all, and an end-of-period cancel leaves
            status='active', so following it would still have 409'd on subscribe. */}
        {golonganLocked ? (
          <div className="rounded-lg border border-warning/300 bg-warning/50 px-4 py-3 text-sm text-warning/700">
            Your{' '}
            <span className="font-semibold">
              {subscription.pricing_package.name} ({golonganLabel(subscription.pricing_package.golongan_usaha)})
            </span>{' '}
            subscription is still active
            {subscription.covered_until
              ? ` until ${new Date(subscription.covered_until).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
              : ''}
            . Your business segment is now{' '}
            <span className="font-semibold">{golonganLabel(pricing.golongan_usaha)}</span> — plan
            changes are locked until renewal (it switches automatically to the equivalent{' '}
            {golonganLabel(pricing.golongan_usaha)} plan as long as your saldo is sufficient), or
            cancel the subscription to switch now.
          </div>
        ) : currentPackage ? (
          <div className="rounded-lg border border-gray-light/200 bg-white px-4 py-3 text-sm text-gray-light/600">
            Current plan:{' '}
            <span className="font-semibold text-gray-light/900">
              {currentPackage.name} {currentPackage.period_label ? `(${currentPackage.period_label})` : ''}
            </span>
            . Choose another plan to switch — upgrades take effect immediately with a prorated
            credit, downgrades take effect from the next period.
            {scheduledPackage ? (
              <>
                {' '}
                <span className="font-semibold text-gray-light/900">
                  Scheduled to switch to {scheduledPackage.name}
                </span>{' '}
                on {scheduledOn}.{' '}
                <button
                  type="button"
                  onClick={dropScheduledChange}
                  className="font-semibold text-brand/700 underline"
                >
                  Cancel scheduled change
                </button>
              </>
            ) : null}
          </div>
        ) : null}

        {/* ── Plan tiers ── */}
        {loading ? (
          <div className="grid grid-cols-1 gap-5 pt-3 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-80 animate-pulse rounded-2xl bg-gray-light/100" />
            ))}
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-5 ${visiblePackages.length >= 4 ? 'sm:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3'
              }`}
          >
            {visiblePackages.map((pkg) => {
              const active = pkg.id === selectedPlanId
              const price = packagePrice(pkg, packages)
              const cta = ctaFor(pkg, currentPackage, subscription?.pending_package_id, golonganLocked)
              const dark = isRecommended(pkg)
              return (
                <div
                  key={pkg.id}
                  className={`relative flex flex-col overflow-hidden rounded-2xl shadow-xs transition ${dark
                    ? 'bg-gradient-to-b from-brand/800 to-brand/950 text-white lg:-my-2.5'
                    : 'border border-gray-light/200 bg-white'
                    } ${active && !dark ? 'ring-2 ring-brand/600' : ''} ${active && dark ? 'ring-2 ring-warning/300' : ''}`}
                >
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-start justify-between gap-3">
                      <h3
                        className={`text-lg font-semibold ${dark ? 'text-white' : 'text-gray-light/900'}`}
                      >
                        {pkg.name}
                      </h3>
                      {dark ? (
                        <span className="shrink-0 rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-brand/700">
                          Recommended
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-4 flex flex-wrap items-baseline gap-x-1.5">
                      <span
                        className={`whitespace-nowrap text-3xl font-bold tracking-tight ${dark ? 'text-white' : 'text-gray-light/900'}`}
                      >
                        {price.amount}
                      </span>
                      <span
                        className={`whitespace-nowrap text-sm font-medium ${dark ? 'text-white/70' : 'text-gray-light/600'}`}
                      >
                        {price.suffix}
                      </span>
                      {/* Satu-satunya penanda visual bahwa tab Annual mengubah
                          sesuatu. Persennya dihitung dari dua angka yang
                          dua-duanya ada di kartu ini, jadi bisa dicek sendiri.
                          `basis-full` menaruhnya di baris sendiri SELALU:
                          dibiarkan mengalir, ia muat di samping "Rp 990.000"
                          tapi terdorong turun oleh "Rp 1.935.000", dan tinggi
                          header ketiga kartu berhenti sama — tombol Choose
                          this plan tidak lagi sebaris. */}
                      {price.savingPct ? (
                        <span className="mt-1 basis-full">
                          <span
                            className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${dark ? 'bg-white/15 text-white' : 'bg-success/50 text-success/700'}`}
                          >
                            Save {price.savingPct}%
                          </span>
                        </span>
                      ) : null}
                    </div>
                    {/* Dasar pembandingnya disebut terang-terangan. Coretan
                        tanpa keterangan bikin orang menebak angka apa yang
                        dicoret — dan tebakannya biasanya base_langganan. */}
                    {price.monthlyCompare ? (
                      <p className={`mt-1 text-sm ${dark ? 'text-white/70' : 'text-gray-light/600'}`}>
                        <span className="line-through">{price.monthlyCompare}</span>
                        {' '}/ month if billed monthly
                      </p>
                    ) : null}
                    {price.periodTotal ? (
                      <p className={`mt-1 text-sm ${dark ? 'text-white/70' : 'text-gray-light/600'}`}>
                        Billed {price.periodTotal} / {price.periodLabel}
                      </p>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => onSelectPlan(pkg)}
                      disabled={cta.disabled}
                      aria-pressed={active}
                      className={`mt-5 w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition disabled:cursor-default ${cta.disabled
                        ? dark
                          ? 'bg-white/15 text-white/60'
                          : 'bg-gray-light/100 text-gray-light/400'
                        : dark
                          ? 'bg-white text-brand/800 hover:bg-white/90'
                          : 'bg-brand/600 text-white hover:bg-brand/700'
                        }`}
                    >
                      {cta.label}
                    </button>

                    <ul className="mt-5 flex flex-col gap-3 text-sm">
                      <li className="flex items-center justify-between gap-2">
                        <span className={dark ? 'text-white/70' : 'text-gray-light/600'}>
                          Fee enquiry
                        </span>
                        <span className={`font-semibold ${dark ? 'text-white' : 'text-gray-light/900'}`}>
                          {formatIDR(pkg.tarif_enquiry_idr)}
                        </span>
                      </li>
                      {/* Hidden at 0 — PAYG is excluded from bonus by BR-SUB-11. */}
                      {pkg.bonus_kuota > 0 ? (
                        <li className="flex items-center justify-between gap-2">
                          <span className={dark ? 'text-white/70' : 'text-gray-light/600'}>
                            Bonus enquiry
                          </span>
                          <span className={`font-semibold ${dark ? 'text-white' : 'text-gray-light/900'}`}>
                            {pkg.bonus_kuota}x
                          </span>
                        </li>
                      ) : null}
                    </ul>

                    <p
                      className={`mt-auto pt-5 text-xs ${dark ? 'text-white/60' : 'text-gray-light/500'}`}
                    >
                      {pkg.is_payg
                        ? 'Free plan if no other plan is selected.'
                        : 'The next monthly fee will be deducted if your balance is sufficient.'}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Calculator (Figma 15194:107308). Divider-nya milik SINI — satu-satunya
            garis horizontal di frame memisahkan grid kartu dari seksi ini, bukan
            di header. */}
        {!loading && packages.length ? (
          <PlanCalculator
            companyId={companyId}
            packages={packages}
            golongan={pricing?.golongan_usaha}
            period={period}
          />
        ) : null}

        {/* Konfirmasi pembayaran pindah ke modal (Figma 15195:146799). Blok
            checkout inline yang dulu ada di sini — ringkasan tagihan, stepper
            kuota, toggle saldo, panel metode pembayaran — semuanya hidup di
            dalam modal itu sekarang, jadi halaman ini tinggal katalog seperti
            di desain. */}
        <ConfirmChangeModal
          open={changeOpen}
          companyId={companyId}
          pkg={selectedPackage}
          currentPackage={currentPackage}
          onClose={() => setChangeOpen(false)}
          onDone={() => {
            setChangeOpen(false)
            reloadSubscription()
          }}
        />

        <ConfirmPaymentModal
          open={confirmOpen}
          companyId={companyId}
          plan={selectedPackage}
          wallet={wallet}
          onClose={() => setConfirmOpen(false)}
          onPaid={(body) => {
            setConfirmOpen(false)
            // Paid entirely from saldo — terminal receipt screen.
            if (body?.paid_from_saldo) {
              setSettled(body)
              return
            }
            // Gateway payment confirmed in-modal (the QR/VA step polled 'paid').
            // The subscription is active server-side; refresh and return to the
            // company view. No `window.location.href = payment_url` redirect — the
            // QR is rendered in our own web now, so there is no gateway page to
            // land on (fixes the "klik X di Xendit malah muncul berhasil" report).
            reloadSubscription()
            navigate('/company')
          }}
        />
      </div>
    </main>
  )
}
